import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { Skeleton } from '@/components/ui/skeleton';

export interface ReportStateProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;
  // Đang tải lại nhưng còn hiện dữ liệu cũ (keepPreviousData)
  isRefreshing?: boolean;
  // Đang gọi lại (vd. sau khi bấm Thử lại): khoá nút để biết là đang chạy
  isFetching?: boolean;
  onRetry: () => void;
  children: React.ReactNode;
}

export const EMPTY_MESSAGE = 'Không có lượt kích hoạt trong khoảng thời gian này';

const errorMessage = (error: unknown) =>
  error instanceof Error && error.message ? error.message : 'Không tải được báo cáo';

const ReportState: React.FC<ReportStateProps> = ({
  isLoading,
  isError,
  error,
  isEmpty,
  isRefreshing = false,
  isFetching = false,
  onRetry,
  children,
}) => {
  // Keyed on the error object: react-query tạo đối tượng lỗi mới mỗi lần lỗi, render lại không toast lại
  useEffect(() => {
    if (isError) toast.error(errorMessage(error));
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="report-skeleton">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white p-8 rounded-lg border border-red-200 text-center">
        <p className="text-sm text-red-600 mb-4">{errorMessage(error)}</p>
        <button
          type="button"
          onClick={onRetry}
          disabled={isFetching}
          className="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFetching ? 'Đang tải…' : 'Thử lại'}
        </button>
      </div>
    );
  }

  if (isEmpty) {
    return <div className="bg-white p-8 rounded-lg border text-center text-sm text-gray-500">{EMPTY_MESSAGE}</div>;
  }

  // Làm mờ để người xem không tưởng số cũ là kết quả của bộ lọc mới.
  // Luôn cùng một thẻ div: đổi kiểu thẻ khi hết cập nhật sẽ dựng lại bảng, mất trạng thái mở rộng.
  return (
    <div
      aria-busy={isRefreshing || undefined}
      data-testid={isRefreshing ? 'report-refreshing' : undefined}
      className={isRefreshing ? 'opacity-60 transition-opacity' : 'transition-opacity'}
    >
      {children}
    </div>
  );
};

export default ReportState;
